import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, FileText, GitCompareArrows, Gavel, PenLine, Brain, PencilRuler,
  X, Check, Send, MessageSquare,
} from "lucide-react";
import { feedbackEvent, getTeacherReviewDemo } from "@/lib/api";
import EpisodeReview from "@/components/EpisodeReview";

// PUBLIC TEACHER REVIEW — a second, bounded public entry point into The Writing Studio.
// Landing (explains the idea) + an ISOLATED synthetic demo (no DB, no auth, no real student data).
// Sign-up routes into the existing auth hub (?classroom). No new LLM calls, no scoring systems.

const EASE = [0.22, 1, 0.36, 1];
const BRAND = "#8C3A2A";
const AN_ID = "public-teacher-review"; // stable id for reused fire-and-forget analytics events
const track = (event, data) => { try { feedbackEvent(AN_ID, event, data); } catch (_) {} };

const THREE = [
  { Icon: FileText, kicker: "See the writing", body: "Review the student’s work directly — the paragraph they actually produced." },
  { Icon: GitCompareArrows, kicker: "See the development", body: "Compare where the student began with where the writing ended, and examine the evidence of change." },
  { Icon: Gavel, kicker: "Make your own judgment", body: "Use the developmental evidence to support — not replace — your own assessment." },
];

export default function TeacherReviewPublic() {
  const [showDemo, setShowDemo] = useState(() => typeof window !== "undefined" && window.location.pathname.endsWith("/review"));

  useEffect(() => {
    document.title = "Teacher Review | The Writing Studio";
    const meta = document.querySelector('meta[name="description"]') || (() => { const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m; })();
    meta.setAttribute("content", "Explore The Writing Studio's Teacher Review, a developmental writing review tool that helps instructors examine student growth while keeping teacher judgment at the center.");
    track("landing_viewed");
  }, []);

  useEffect(() => {
    const onPop = () => setShowDemo(window.location.pathname.endsWith("/review"));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const openDemo = () => { track("demo_opened"); window.history.pushState({ trDemo: true }, "", "/teacher-review/review"); setShowDemo(true); window.scrollTo({ top: 0 }); };
  const goSignUp = (where) => { track("signup_clicked", { where }); window.location.href = "/?classroom"; };
  const goSignIn = () => { track("signin_clicked"); window.location.href = "/?classroom"; };

  if (showDemo) return <TeacherReviewDemo onExit={() => window.history.back()} onSignUp={() => goSignUp("demo")} />;

  return (
    <div className="min-h-screen paper-grain text-stone-900" data-testid="teacher-review-public">
      {/* Minimal, bounded nav */}
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-[#faf9f6]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 h-16 flex items-center justify-between">
          <a href="/teacher-review" className="flex items-center gap-3" data-testid="trp-logo">
            <img src="/cgi-logo.png" alt="Common Ground Institute" className="h-7 w-auto" />
            <span className="hidden sm:inline-block h-5 w-px bg-stone-300" />
            <span className="hidden sm:inline font-serif-display text-lg tracking-tight text-stone-800">The Writing Studio</span>
            <span className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2 py-0.5">Teacher Review</span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={goSignIn} data-testid="trp-nav-signin" className="font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-700 hover:text-[#8C3A2A] transition-colors">Sign in</button>
            <button onClick={() => goSignUp("nav")} data-testid="trp-nav-signup" className="inline-flex items-center gap-2 border border-stone-900 px-4 py-2 font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-900 transition-colors hover:bg-stone-900 hover:text-stone-50">Sign up</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Hero */}
        <section className="pt-20 md:pt-28 pb-14 md:pb-20">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
            className="font-mono-panel text-xs sm:text-sm uppercase tracking-[0.22em] text-[#8C3A2A] font-semibold mb-6">
            The Writing Studio · Teacher Review
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.06, ease: EASE }}
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] text-stone-900 max-w-4xl">
            See how a student’s writing developed.
            <span className="block text-[#8C3A2A]">Keep teacher judgment at the center.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="text-lg sm:text-xl text-stone-700 leading-relaxed mt-7 max-w-2xl">
            Teacher Review organizes the writing, developmental evidence, and instructor assessment in one place — while the teacher makes the final judgment.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.24, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={() => { track("try_clicked", { where: "hero" }); openDemo(); }} data-testid="trp-try-hero"
              className="group inline-flex items-center gap-3 bg-[#8C3A2A] text-stone-50 px-7 py-4 font-mono-panel text-xs uppercase tracking-[0.2em] transition-colors hover:bg-[#a34635]">
              Try Teacher Review
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => goSignUp("hero")} data-testid="trp-signup-hero"
              className="inline-flex items-center gap-2 border border-stone-900 px-6 py-4 font-mono-panel text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors hover:bg-stone-900 hover:text-stone-50">
              Sign up
            </button>
          </motion.div>
          <p className="mt-5 font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-400">Public beta · Demo data only</p>
        </section>

        {/* Short explanation */}
        <section className="border-t border-stone-200 py-14 md:py-16 max-w-3xl">
          <p className="text-lg text-stone-700 leading-relaxed">
            The Writing Studio helps students develop their own writing rather than writing for them. Teacher Review gives instructors a concise view of the student’s writing, how it changed, the evidence of development, and the teacher’s own assessment.
          </p>
          <p className="mt-4 font-serif-display text-2xl text-stone-900">The system supports judgment. It does not make the teacher’s judgment for them.</p>
        </section>

        {/* Three-part explanation */}
        <section className="border-t border-stone-200 py-14 md:py-16" data-testid="trp-three-part">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {THREE.map(({ Icon, kicker, body }, i) => (
              <div key={i}>
                <Icon className="h-6 w-6 text-[#8C3A2A]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl mt-4 text-stone-900">{kicker}</h3>
                <p className="text-base text-stone-600 leading-relaxed mt-2">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Philosophy */}
        <section className="border-t border-stone-200 py-14 md:py-16 max-w-3xl" data-testid="trp-philosophy">
          <p className="font-mono-panel text-xs uppercase tracking-[0.22em] text-stone-500 mb-6">Development, not just evaluation</p>
          <p className="font-serif-display text-2xl md:text-3xl leading-snug text-stone-900">
            The Writing Studio is built around a simple idea: students develop as writers by doing the intellectual work of writing themselves.
          </p>
          <p className="text-lg text-stone-700 leading-relaxed mt-5">
            Technology can help make that development visible, but it should not replace the student’s work or the teacher’s judgment. Writing assessment should do more than produce a score — teachers need to see what the student accomplished, what changed, what remains underdeveloped, and what evidence supports those judgments.
          </p>
        </section>

        {/* Try it */}
        <section className="border-t border-stone-200 py-14 md:py-16 text-center" data-testid="trp-tryit">
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-stone-900">Explore it yourself</h2>
          <p className="text-base text-stone-600 mt-3 max-w-xl mx-auto">Open a sample review with a preloaded student example. Nothing to set up.</p>
          <button onClick={() => { track("try_clicked", { where: "tryit" }); openDemo(); }} data-testid="trp-launch-mid"
            className="group mt-7 inline-flex items-center gap-3 bg-[#8C3A2A] text-stone-50 px-7 py-4 font-mono-panel text-xs uppercase tracking-[0.2em] transition-colors hover:bg-[#a34635]">
            Launch Teacher Review
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </section>

        {/* Why we built it */}
        <section className="border-t border-stone-200 py-14 md:py-16 max-w-3xl" data-testid="trp-why">
          <p className="font-mono-panel text-xs uppercase tracking-[0.22em] text-stone-500 mb-6">Why we built Teacher Review</p>
          <p className="text-lg text-stone-700 leading-relaxed">
            Most writing tools focus on producing better text. The Writing Studio focuses on developing better writers. Teacher Review is designed to help instructors see the student’s work and development more clearly while preserving the instructor’s role as teacher and evaluator.
          </p>
        </section>

        {/* Public beta + feedback */}
        <section className="border-t border-stone-200 py-14 md:py-16 max-w-3xl" data-testid="trp-beta">
          <p className="font-mono-panel text-xs uppercase tracking-[0.22em] text-[#8C3A2A] mb-4">Public beta</p>
          <p className="text-base text-stone-700 leading-relaxed">
            Teacher Review is an evolving part of The Writing Studio. This demo is designed for exploration and feedback. It uses sample data and shows only a limited portion of the full platform — the student writing experience and other teacher tools are not included here.
          </p>
          <FeedbackInline />
        </section>
      </main>

      {/* Closing CTA */}
      <section className="border-t border-stone-200 bg-[#f2f0ed]" data-testid="trp-closing">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-stone-900">Try Teacher Review</h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => { track("try_clicked", { where: "closing" }); openDemo(); }} data-testid="trp-launch-closing"
              className="group inline-flex items-center gap-3 bg-[#8C3A2A] text-stone-50 px-7 py-4 font-mono-panel text-xs uppercase tracking-[0.2em] transition-colors hover:bg-[#a34635]">
              Launch demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => goSignUp("closing")} data-testid="trp-signup-closing"
              className="inline-flex items-center gap-2 border border-stone-900 px-6 py-4 font-mono-panel text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors hover:bg-stone-900 hover:text-stone-50">
              Sign up
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200 bg-[#faf9f6]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 py-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <img src="/cgi-logo.png" alt="Common Ground Institute" className="h-8 w-auto" />
          <p className="font-mono-panel text-[11px] uppercase tracking-[0.16em] text-stone-400">A nonprofit project of the Common Ground Institute</p>
        </div>
      </footer>
    </div>
  );
}

function FeedbackInline() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const submit = () => { track("feedback_submitted", { text: text.slice(0, 500) }); setSent(true); setText(""); };
  if (sent) return <p className="mt-6 text-[13px] text-emerald-700" data-testid="trp-feedback-thanks">Thank you — your feedback helps shape Teacher Review.</p>;
  return (
    <div className="mt-6">
      {!open ? (
        <button onClick={() => { track("feedback_clicked"); setOpen(true); }} data-testid="trp-feedback-open"
          className="inline-flex items-center gap-2 font-mono-panel text-[11px] uppercase tracking-[0.18em] text-[#8C3A2A] hover:text-[#a34635]">
          <MessageSquare className="h-4 w-4" /> Share feedback
        </button>
      ) : (
        <div className="mt-2 max-w-lg">
          <textarea data-testid="trp-feedback-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="What did you notice? What would help teachers most?"
            className="w-full rounded-md border border-stone-300 p-3 text-[14px] text-stone-800 outline-none focus:border-[#8C3A2A]" />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="trp-feedback-send" onClick={submit} disabled={!text.trim()}
              className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12px] px-4 py-2 rounded-md disabled:opacity-40"><Send className="h-3.5 w-3.5" /> Send</button>
            <button onClick={() => setOpen(false)} className="text-[12px] text-stone-500 px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ISOLATED DEMO — synthetic sample student. No fetch, no auth, no real data.
// ---------------------------------------------------------------------------
const DEMO = {
  assignment: "Explain how one choice a character makes in the story reveals who they are.",
  student: "Sample student",
  beginning: "In the story the main character makes a choice and it shows what they are like. She gives up her seat on the lifeboat. This shows she is nice. It was a good thing to do and the author wanted us to see she is a good person because she helped other people instead of herself.",
  final: "When Mara gives up her seat on the lifeboat, the choice reveals more than kindness — it reveals that she measures her own worth by whether others survive. She does not hesitate, and she does not explain herself. That silence matters: a person who needed to be seen as good would have said something. Mara’s choice shows a self that has already decided other lives outrank her own, which is why the moment feels less like sacrifice to her than like simply doing what she is.",
  changes: [
    "Names the character and the specific choice instead of speaking generally about “the main character.”",
    "Moves from a value judgment (“she is nice”) to an interpretation of character (“she measures her own worth by whether others survive”).",
    "Uses a detail — her silence — as evidence, and explains what it means.",
    "Answers the assignment’s real question (what the choice reveals) rather than whether the choice was good.",
  ],
  writingStructure: "The final paragraph opens with the claim, grounds it in a specific moment, then develops a single line of reasoning to a controlled close. Earlier, the sentences listed observations without connecting them.",
  thinkingStructure: "The student moved from evaluating the choice (good/bad) to interpreting it (what it reveals about a self). The reasoning now holds one idea and pursues its implications instead of restating it.",
};

const DIMENSIONS = [
  { id: "thought", name: "Quality of thought", hint: "Depth and control of the central interpretation" },
  { id: "evidence", name: "Use of evidence", hint: "Grounding the claim in specifics from the text" },
  { id: "organization", name: "Organization", hint: "How the paragraph holds together" },
];
const LEVELS = ["Beginning", "Developing", "Proficient", "Advanced"];

function TeacherReviewDemo({ onExit, onSignUp }) {
  const [vals, setVals] = useState({});
  const [comments, setComments] = useState({});
  const [overall, setOverall] = useState("");
  const [saved, setSaved] = useState(false);
  const setV = (id, v) => { setVals((p) => ({ ...p, [id]: v })); setSaved(false); };

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div className="min-h-screen paper-grain text-stone-900" data-testid="teacher-review-demo">
      {/* Demo banner */}
      <div className="sticky top-0 z-30 bg-[#8C3A2A] text-stone-50">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-11 flex items-center justify-between">
          <span data-testid="trp-demo-badge" className="font-mono-panel text-[11px] uppercase tracking-[0.2em]">Public beta — sample student · demo data only</span>
          <button onClick={onExit} data-testid="trp-demo-exit" className="inline-flex items-center gap-1 font-mono-panel text-[11px] uppercase tracking-[0.18em] hover:text-stone-200"><X className="h-4 w-4" /> Back</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">Teacher Review</p>
        <h1 className="font-serif-display text-3xl md:text-4xl tracking-tight text-stone-900">Reviewing {DEMO.student}’s writing</h1>

        {/* REAL Teacher Review (EpisodeReview) rendered against safe seeded demo data — same shared
            review architecture used in authenticated Teacher Admin, only the DATA CONTEXT differs.
            (Back lives in the demo banner above; assignment renders inside EpisodeReview.) */}
        <section className="mt-4" data-testid="demo-real-review">
          <EpisodeReview fetcher={getTeacherReviewDemo} demo />
        </section>

        {/* 3. Make your own judgment — DEMO-ONLY InstructorAssessment presentation (interactive, local only,
            never saved). This is NOT a live teacher-entered assessment; the real InstructorAssessment runs
            in authenticated Teacher Admin. */}
        <section className="mt-10" data-testid="demo-assessment" aria-labelledby="d3">
          <div className="flex items-center gap-2 mb-1"><Gavel className="h-4 w-4 text-[#8C3A2A]" /><h2 id="d3" className="font-serif-display text-2xl text-stone-900">Instructor Assessment <span className="align-middle ml-2 font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2 py-0.5">Demo preview</span></h2></div>
          <p className="text-[13px] text-stone-500 mb-4">A preview of how a teacher records their own judgment. In the full product, the teacher enters this in Teacher Admin and it saves to their record. This demo panel is illustrative only — it is not a live teacher-entered assessment and nothing is saved.</p>
          <div className="rounded-md border border-stone-200 bg-white p-4" style={{ borderTopColor: BRAND, borderTopWidth: 2 }}>
            <div className="space-y-4">
              {DIMENSIONS.map((d) => (
                <div key={d.id} data-testid={`demo-dim-${d.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <label className="text-[14px] font-medium text-stone-800">{d.name}<span className="block text-[11px] font-normal text-stone-400">{d.hint}</span></label>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label={d.name}>
                    {LEVELS.map((lvl) => (
                      <button key={lvl} data-testid={`demo-dim-${d.id}-${lvl}`} role="radio" aria-checked={vals[d.id] === lvl}
                        onClick={() => setV(d.id, lvl)}
                        className={`px-3 py-1.5 rounded-full border text-[12.5px] transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C3A2A] ${vals[d.id] === lvl ? "bg-[#8C3A2A] text-white border-[#8C3A2A]" : "border-stone-300 text-stone-700 hover:border-[#8C3A2A]"}`}>
                        {lvl}
                      </button>
                    ))}
                  </div>
                  <input aria-label={`Comment for ${d.name}`} value={comments[d.id] || ""} onChange={(e) => { setComments((p) => ({ ...p, [d.id]: e.target.value })); setSaved(false); }}
                    placeholder="Comment (optional)" className="mt-2 block w-full rounded-md border border-stone-200 p-2 text-[12.5px] outline-none focus:ring-2 focus:ring-[#8C3A2A]" />
                </div>
              ))}
              <div className="border-t border-stone-100 pt-3">
                <label htmlFor="demo-overall" className="text-[14px] font-medium text-stone-800 flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5 text-stone-500" /> Overall comment to the student</label>
                <textarea id="demo-overall" data-testid="demo-overall" value={overall} onChange={(e) => { setOverall(e.target.value); setSaved(false); }} rows={3}
                  placeholder="What you want the student to take away…" className="mt-2 block w-full rounded-md border border-stone-200 p-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#8C3A2A]" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button data-testid="demo-save" onClick={() => setSaved(true)} className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] px-4 py-2 rounded-md hover:bg-[#a34635]"><Check className="h-4 w-4" /> Record assessment</button>
              {saved && <span data-testid="demo-saved-note" className="text-[12.5px] text-emerald-700">Recorded for this demo — in the full product this saves to your record.</span>}
            </div>
          </div>
        </section>

        {/* Sign-up prompt after interacting */}
        <section className="mt-12 rounded-lg border border-stone-200 bg-[#f2f0ed] p-6 text-center" data-testid="demo-signup-cta">
          <h3 className="font-serif-display text-2xl text-stone-900">Want to use Teacher Review with your own students?</h3>
          <p className="text-[14px] text-stone-600 mt-2 max-w-lg mx-auto">Create an account to bring your class’s writing into Teacher Review.</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button onClick={onSignUp} data-testid="demo-signup" className="inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-6 py-3 font-mono-panel text-[11px] uppercase tracking-[0.2em] hover:bg-[#a34635]">Create an account</button>
            <button onClick={onExit} data-testid="demo-back-landing" className="font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-600 hover:text-stone-900">Back to overview</button>
          </div>
        </section>
      </div>
    </div>
  );
}
